import React, { useRef, useState, useEffect, useCallback } from 'react';
import { signatureService } from '../services/api';
import { toast } from 'react-hot-toast';

interface SignaturePadProps {
  onSignatureCapture?: (signature: string) => void;
  readonly?: boolean;
  displaySignature?: string;
  inkColor?: string;
}

export const SignaturePad: React.FC<SignaturePadProps> = ({ 
  onSignatureCapture, 
  readonly = false,
  displaySignature,
  inkColor = '#0f172a'
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [savedSignature, setSavedSignature] = useState<string | null>(displaySignature || null);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [isWideMode, setIsWideMode] = useState(false);
  const [isLandscape, setIsLandscape] = useState(false);

  // Monitor device orientation
  useEffect(() => {
    const checkOrientation = () => {
      if (typeof window !== 'undefined') {
        const isLand = window.innerWidth > window.innerHeight;
        setIsLandscape(isLand);
      }
    };
    checkOrientation();
    window.addEventListener('resize', checkOrientation);
    window.addEventListener('orientationchange', checkOrientation);
    return () => {
      window.removeEventListener('resize', checkOrientation);
      window.removeEventListener('orientationchange', checkOrientation);
    };
  }, []);

  // Setup canvas with high-DPI scaling
  const setupCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || readonly) return;

    const rect = canvas.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 2.5);

    // Set internal resolution
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;

    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.scale(dpr, dpr);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.lineWidth = 2.5;
      ctx.strokeStyle = inkColor;
    }
  }, [readonly, inkColor]);

  useEffect(() => {
    setupCanvas();
    const handleResize = () => setupCanvas();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [setupCanvas, isWideMode]);

  // Coordinates helper translating mouse/touch to canvas logical points
  const getCanvasCoords = (clientX: number, clientY: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return {
      x: clientX - rect.left,
      y: clientY - rect.top,
    };
  };

  // Mouse handlers
  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (readonly) return;
    const { x, y } = getCanvasCoords(e.clientX, e.clientY);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.beginPath();
      ctx.moveTo(x, y);
      setIsDrawing(true);
      setHasDrawn(true);
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing || readonly) return;
    const { x, y } = getCanvasCoords(e.clientX, e.clientY);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.lineTo(x, y);
      ctx.stroke();
    }
  };

  const handleMouseUp = () => {
    setIsDrawing(false);
  };

  // Mobile Touch handlers (Preventing pull-to-refresh & screen scrolling)
  const handleTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
    if (readonly || e.touches.length === 0) return;
    e.preventDefault();
    const touch = e.touches[0];
    const { x, y } = getCanvasCoords(touch.clientX, touch.clientY);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.beginPath();
      ctx.moveTo(x, y);
      setIsDrawing(true);
      setHasDrawn(true);
    }
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing || readonly || e.touches.length === 0) return;
    e.preventDefault();
    const touch = e.touches[0];
    const { x, y } = getCanvasCoords(touch.clientX, touch.clientY);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.lineTo(x, y);
      ctx.stroke();
    }
  };

  const handleTouchEnd = (e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        setSavedSignature(null);
        setHasDrawn(false);
      }
    }
  };

  const saveSignature = async () => {
    const canvas = canvasRef.current;
    if (!canvas || !hasDrawn) {
      toast.error('Please sign on the pad before saving.');
      return;
    }

    const signatureData = canvas.toDataURL('image/png');
    setSavedSignature(signatureData);

    if (onSignatureCapture) {
      onSignatureCapture(signatureData);
    }

    try {
      await signatureService.uploadSignature(signatureData);
      toast.success('Signature securely stored and verified!');
      if (isWideMode) {
        setIsWideMode(false);
      }
    } catch (error) {
      console.error('Failed to save signature:', error);
      toast.error('Could not upload signature to cloud server.');
    }
  };

  return (
    <div ref={containerRef} className="w-full font-sans">
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs font-bold text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-orange-400"></span>
          {displaySignature ? 'Verified Digital Signature' : 'Technician / Manager Signature'}
        </label>
        {!readonly && !displaySignature && (
          <div className="flex items-center gap-2">
            {!isLandscape && (
              <button
                type="button"
                onClick={() => setIsWideMode(!isWideMode)}
                className="text-[11px] font-bold text-orange-400 hover:text-orange-300 transition flex items-center gap-1 px-2.5 py-1 rounded-full bg-orange-500/10 border border-orange-500/20"
                title="Expand signature pad for wide signing"
              >
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
                </svg>
                <span>{isWideMode ? 'Standard Pad' : 'Wide Pad'}</span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Mobile Orientation Advice Banner */}
      {!readonly && !displaySignature && !isLandscape && !isWideMode && (
        <div className="mb-2 text-[10px] text-slate-400 flex items-center gap-1.5 bg-slate-900/60 px-3 py-1.5 rounded-xl border border-white/[0.06] sm:hidden">
          <span>🔄</span>
          <span>Tip: Hold device in <strong>Landscape</strong> or tap <strong>Wide Pad</strong> for a wider signing space.</span>
        </div>
      )}

      {/* Main Canvas Container */}
      <div className={`relative rounded-2xl border transition-all ${
        isWideMode
          ? 'fixed inset-4 z-50 bg-slate-950/95 backdrop-blur-xl border-orange-500/40 p-4 flex flex-col justify-between shadow-2xl'
          : 'glass-card border-white/10 p-3 sm:p-4'
      }`}>
        {isWideMode && (
          <div className="flex items-center justify-between pb-3 border-b border-white/10 mb-3">
            <div>
              <h3 className="text-sm font-bold text-white">Full-Width Signature Canvas</h3>
              <p className="text-[11px] text-slate-400">Draw your signature clearly using your finger or stylus</p>
            </div>
            <button
              type="button"
              onClick={() => setIsWideMode(false)}
              className="text-slate-400 hover:text-white text-lg px-2 py-1"
            >
              ✕
            </button>
          </div>
        )}

        {!displaySignature ? (
          <>
            <div className="relative rounded-xl overflow-hidden bg-slate-100 border border-slate-300 shadow-inner">
              <canvas
                ref={canvasRef}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onTouchCancel={handleTouchEnd}
                className={`w-full touch-none select-none block ${
                  readonly ? 'opacity-50 cursor-not-allowed' : 'cursor-crosshair'
                }`}
                style={{
                  height: isWideMode ? 'calc(100vh - 220px)' : isLandscape ? '160px' : '190px',
                  touchAction: 'none',
                }}
              />

              {/* Watermark Guidelines for Signatures */}
              <div className="absolute bottom-6 left-6 right-6 border-b border-dashed border-slate-300 pointer-events-none flex justify-between items-center text-[10px] text-slate-400 font-mono select-none">
                <span>Sign Above Line &bull; Official Digital Seal</span>
                <span className="text-[9px] uppercase tracking-wider text-orange-500/80">Verisure Secure</span>
              </div>
            </div>

            {!readonly && (
              <div className="flex flex-wrap items-center justify-between gap-3 mt-3.5">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={clearSignature}
                    className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold rounded-xl text-xs transition flex items-center gap-1.5 border border-slate-700 active:scale-95"
                  >
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                    </svg>
                    <span>Clear</span>
                  </button>
                  {isWideMode && (
                    <button
                      type="button"
                      onClick={() => setIsWideMode(false)}
                      className="px-3 py-2 text-slate-400 hover:text-white text-xs font-semibold"
                    >
                      Exit Wide Pad
                    </button>
                  )}
                </div>

                <button
                  type="button"
                  onClick={saveSignature}
                  disabled={!hasDrawn}
                  className="px-5 py-2.5 bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 hover:opacity-95 text-white font-extrabold rounded-xl text-xs shadow-lg shadow-orange-500/25 transition disabled:opacity-40 flex items-center gap-2 active:scale-95"
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  <span>Save Signature</span>
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="bg-slate-900 rounded-xl p-4 flex flex-col items-center justify-center border border-white/10">
            <img
              src={displaySignature}
              alt="Signature"
              className="w-full h-auto max-h-36 object-contain filter drop-shadow-[0_0_8px_rgba(255,90,0,0.25)]"
            />
            <div className="text-[10px] text-emerald-400 font-bold mt-2 flex items-center gap-1">
              <span>✓</span> Cryptographically Verified
            </div>
          </div>
        )}
      </div>

      {savedSignature && !displaySignature && (
        <div className="mt-2.5 p-2 bg-emerald-500/10 border border-emerald-500/20 rounded-xl flex items-center gap-2">
          <span className="text-emerald-400 text-xs">✓</span>
          <p className="text-xs text-emerald-300 font-semibold">Signature ready and active for document verification.</p>
        </div>
      )}
    </div>
  );
};
